import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useAuthContext } from '../context/AuthContext';

export default function Login() {
  const { user, authReady } = useAuth();
  const { signIn, register, sendPasswordResetEmail, loading, error } = useAuthContext();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const from =
    (location.state as { from?: string })?.from || `/${location.search}`;

  const wasRedirected = location.state && (location.state as { from?: string })?.from;

  useEffect(() => {
    if (authReady && user?.uid) {
      navigate(from);
    }
  }, [authReady, user?.uid, from, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isRegister) {
        await register(email, password);
      } else {
        await signIn(email, password);
      }
      navigate(from);
    } catch {
      // error handled in context
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError(null);
    setResetSuccess(false);

    try {
      await sendPasswordResetEmail(resetEmail);
      setResetSuccess(true);
      setResetEmail('');
      // Auto-close after 3 seconds
      setTimeout(() => {
        setShowResetPassword(false);
        setResetSuccess(false);
      }, 3000);
    } catch (err: any) {
      setResetError(err.message || 'Failed to send password reset email');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto pt-20 space-y-4">
      {/* Password Reset Modal */}
      {showResetPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Reset Password</h2>
              <button
                onClick={() => {
                  setShowResetPassword(false);
                  setResetError(null);
                  setResetSuccess(false);
                  setResetEmail('');
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {resetSuccess ? (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                <p className="text-sm">
                  <strong>✓ Request Sent!</strong>
                  <br />
                  If this email is registered with us, you'll receive password reset instructions shortly.
                  <br />
                  <span className="text-xs text-gray-600 mt-1 block">
                    Don't see it? Check your spam folder.
                  </span>
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  Enter your email address and we'll send you a link to reset your password.
                </p>

                <form onSubmit={handlePasswordReset} className="space-y-4">
                  <input
                    type="email"
                    className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="your@email.com"
                    value={resetEmail}
                    onChange={e => setResetEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />

                  {resetError && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                      <p className="text-sm">{resetError}</p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowResetPassword(false);
                        setResetError(null);
                        setResetEmail('');
                      }}
                      className="flex-1 border border-gray-300 text-gray-700 p-2 rounded hover:bg-gray-50"
                      disabled={resetLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-purple-500 text-white p-2 rounded hover:bg-purple-600 disabled:bg-purple-300"
                      disabled={resetLoading || !resetEmail}
                    >
                      {resetLoading ? 'Sending...' : 'Send Reset Link'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
      {wasRedirected && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg mb-4">
          <p className="text-sm">
            <strong>Authentication Required</strong>
            <br />
            Please sign in to access that page.
          </p>
        </div>
      )}
      <h1 className="text-2xl font-bold text-center">
        {isRegister ? 'Register' : 'Login'}
      </h1>
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="email"
          className="w-full border p-2 rounded"
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          autoComplete="email"
          required
        />
        <input
          type="password"
          className="w-full border p-2 rounded"
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          autoComplete={isRegister ? "new-password" : "current-password"}
          required
        />
        {error && <p className="text-red-500 text-sm">{error}</p>}
        <button
          type="submit"
          className="w-full bg-purple-500 text-white p-2 rounded"
          disabled={loading}
        >
          {loading ? 'Loading...' : isRegister ? 'Register' : 'Login'}
        </button>
      </form>

      {/* Forgot Password Link - Only show on login (not register) */}
      {!isRegister && (
        <div className="text-center">
          <button
            type="button"
            onClick={() => setShowResetPassword(true)}
            className="text-sm text-purple-500 hover:text-purple-700 hover:underline"
          >
            Forgot your password?
          </button>
        </div>
      )}

      <p className="text-center text-sm">
        {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
        <button
          type="button"
          className="text-purple-500"
          onClick={() => setIsRegister(!isRegister)}
        >
          {isRegister ? 'Login' : 'Register'}
        </button>
      </p>
    </div>
  );
}

